/**
 * When an opener group's two VENUE ACCOUNTS stopped being knowable from the
 * cached `opener-groups` row.
 *
 * The pair can be chosen at START time and is only observable through that row,
 * which polls every 30s. So between pressing Start and the next successful
 * fetch, the cached row still describes the PREVIOUS run — and anything reading
 * it would present the residual account's numbers as the position of a group
 * that is now trading somewhere else.
 *
 * A mark invalidates the WHOLE pair, never one venue's half: half a pair looks
 * plausible and would still pass an equality check on the half that agrees.
 *
 * Invalidating the query is not enough on its own: the refetch can be in
 * flight, or fail. The group is marked here BEFORE the start request leaves AND
 * again once it has settled (the server writes the account ids during the
 * request, so only a mark taken after the response can be said to postdate the
 * write), and stays marked until the group list is holding rows from a fetch
 * that STARTED after the mark.
 *
 * The other half of that comparison is NOT kept here: it is `fetchStartedAt`
 * on the cached `opener-groups` value itself (see `OpenerGroupList`), so it
 * describes the rows on screen by construction.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

export interface GroupAccountResolution {
  /** groupId → epoch ms at which its account stopped being knowable. */
  marks: ReadonlyMap<string, number>;
}

let snapshot: GroupAccountResolution = { marks: new Map() };

/** Snapshot for `useSyncExternalStore`; identity changes only on a real change. */
export const getGroupAccountResolution = (): GroupAccountResolution => snapshot;

export function subscribeGroupAccountResolution(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const publish = (next: GroupAccountResolution): void => {
  snapshot = next;
  for (const listener of listeners) listener();
};

export function markGroupAccountUnresolved(groupId: string): void {
  const marks = new Map(snapshot.marks);
  marks.set(groupId, Date.now());
  publish({ ...snapshot, marks });
}
